<template>
    <div class="relative">
        <div class="flex items-center">
            <button
                type="button"
                class="relative w-full border-l border-t border-b border-default-300 focus:border-highlight-300 focus:outline-none focus:ring focus:ring-highlight-200 focus:ring-opacity-50 rounded-l-md shadow-sm flex-grow"
                :class="{
                    'border-r': !clearable || !value,
                    'rounded-r-md': !clearable || !value,
                    'h-10': !small,
                    'h-6': small,
                    'text-xs': true,
                }"
                @click="menuOpen = !menuOpen"
                ref="selectButton"
                @keydown="onKeyDown"
                @keydown.enter="onItemEnter"
                @keydown.escape="menuOpen = false"
            >
                <span class="flex items-center pr-6">
                    <span :class="{'ml-3': true, 'block': true, 'text-default-400': !selectedText, 'italic': !selectedText}" v-html="selectedText || placeholder" />
                </span>
                <span class="ml-3 absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg class="h-5 w-5 text-default-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#00A07F" aria-hidden="true">
                        <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd">
                        </path>
                    </svg>
                </span>
            </button>
            <button v-if="clearable && !!value" @click.stop.prevent="clear" class="h-10 border-r border-t border-b border-default-300 focus:border-highlight-300 focus:outline-none focus:ring focus:ring-highlight-200 focus:ring-opacity-50 rounded-r-md shadow-sm flex items-center font-normal" :title="__('global.clear')">
                <icon name="clear" />
            </button>
        </div>
        <div v-if="menuOpen" class="absolute mt-1 w-full z-10 rounded-md bg-white shadow-lg" ref="panelResultElement">
            <div class="flex" v-if="searchable">
                <form-input
                    type="text"
                    class="m-3 block w-full"
                    v-model="search"
                    :placeholder="__('global.search')"
                    @keydown="onKeyDown"
                    @keydown.enter="onItemEnter"
                    @keydown.escape="menuOpen = false"
                    ref="searchInput"
                />
            </div>
            <ul tabindex="-1" role="listbox" aria-labelledby="listbox-label" aria-activedescendant="listbox-item-3" class="max-h-56 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                <li
                    v-for="(item, index) in displayedItems"
                    :key="item[itemValue]"
                    role="option"
                    class="text-default-900 cursor-default select-none hover:bg-highlight-500 hover:text-white relative pr-9"
                    :class="{
                        'bg-highlight-500': focusedIndex === index,
                        'text-white': focusedIndex === index,
                        'text-xs': true,
                        'py-2': !small,
                        'pl-3': !small,
                        'pl-1': small,
                        'py-1': small,
                    }"
                    @click.prevent="(multiple ? toggleValue : setValue)(item[itemValue])"
                >
                    <div class="flex items-center">
                        <span class="block font-normal" :class="{'ml-3': !small, 'ml-1': small}" v-html="item[itemText]" />
                    </div>
                    <span v-if="multiple ? (value.indexOf(item[itemValue]) !== -1) : (item[itemValue] === value)" class="absolute inset-y-0 right-0 flex items-center" :class="{'pr-4': !small, 'pr-2': small}">
                        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#00A07F" aria-hidden="true">
                            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd">
                            </path>
                        </svg>
                    </span>
                </li>
            </ul>
        </div>

        <div v-if="!!help && help[0] != '$'" class="mt-2 text-xs text-default-500" v-html="help" />
    </div>
</template>

<script>
export default {
    props: {
        value: {},
        items: {
            type: Array,
            required: true,
        },
        itemValue: {
            type: String,
            default: 'value',
        },
        itemText: {
            type: String,
            default: 'text',
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        searchCallback: {
            type: Function,
        },
        selectedItem: Object,
        hideValues: {
            type: Array,
            default: () => [],
        },
        small: Boolean,
        placeholder: String,
        help: String,
    },
    emits: ['input', 'inputObject'],
    data() {
        return {
            menuOpen: false,
            search: '',
            focusedIndex: -1,
        };
    },
    computed: {
        selected() {
            if (this.multiple) {
                return this.items.filter((i) => this.value.indexOf(i[this.itemValue]) !== -1);
            } else {
                if (!!this.selectedItem) {
                    return this.selectedItem;
                } else if (!!this.value || this.value === 0) {
                    return this.items.find((i) => i[this.itemValue] === this.value);
                } else {
                    return null;
                }
            }
        },
        selectedText() {
            if (this.multiple) {
                return this.selected.map((i) => i[this.itemText]).join(', ');
            } else {
                if (!!this.selected) {
                    return this.selected[this.itemText];
                } else {
                    return '';
                }
            }
        },
        displayedItems() {
            let res;
            if (!!this.search && !this.searchCallback) {
                const words = this.search.split(' ').filter((w) => !!w);
                res = this.items.filter((item) => {
                    for (let i = 0; i < words.length; i++) {
                        if (item[this.itemText].toLowerCase().indexOf(words[i].toLowerCase()) === -1) {
                            return false;
                        }
                    };
                    return true;
                });
            } else {
                res = this.items;
            }
            return res.filter((item) => {
                return this.hideValues.indexOf(item[this.itemValue]) === -1;
            });
        },
    },
    watch: {
        menuOpen(newValue, oldValue) {
            if (newValue && !oldValue && this.searchable) {
                this.focusedIndex = -1;
                this.search = '';
                this.focusSearchInput();
            }
        },
        search(newValue, oldValue) {
            if (newValue !== oldValue) {
                if (!!this.searchCallback) {
                    this.searchCallback(newValue);
                }
                this.focusedIndex = -1;
            }
        },
    },
    created() {
        document.addEventListener('mousedown', this.handleClickOutside);
    },
    unmounted() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    },
    methods: {
        focusSearchInput() {
            this.$waitForRef('searchInput').then((input) => {
                input.focus();
            });
        },
        setValue(value) {
            this.$emit('input', value);
            this.menuOpen = false;
            this.search = '';
            this.$nextTick(() => {
                this.$emit('inputObject', this.selected);
            });
        },
        toggleValue(value) {
            if (this.value.indexOf(value) === -1) {
                this.$emit('input', [...this.value, value]);
            } else {
                this.$emit('input', this.value.filter((v) => v !== value));
            }
            this.search = '';
        },
        handleClickOutside(event) {
            if (
                !!this.$refs.panelResultElement &&
                !!this.$refs.selectButton &&
                !this.$refs.panelResultElement.contains(event.target) &&
                !this.$refs.selectButton.contains(event.target)
            ) {
                this.menuOpen = false;
            }
        },
        onKeyDown(event) {
            if (event.code == 'ArrowDown') {
                this.menuOpen = true;
                this.focusedIndex += 1;
                if (this.focusedIndex > this.displayedItems.length - 1) {
                    this.focusedIndex = 0;
                }
                event.stopPropagation();
                event.preventDefault();
            }
            if (event.code == 'ArrowUp') {
                this.menuOpen = true;
                this.focusedIndex -= 1;
                if (this.focusedIndex < 0) {
                    this.focusedIndex = this.displayedItems.length - 1;
                }
                event.stopPropagation();
                event.preventDefault();
            }
            if (event.code == 'Space' && (!this.searchable || !this.menuOpen)) {
                this.menuOpen = !this.menuOpen;
                event.stopPropagation();
                event.preventDefault();
            }
        },
        onItemEnter(event) {
            if (this.menuOpen && this.focusedIndex >= 0) {
                if (this.multiple) {
                    this.toggleValue(this.displayedItems[this.focusedIndex][this.itemValue]);
                } else {
                    this.setValue(this.displayedItems[this.focusedIndex][this.itemValue]);
                }
                event.stopPropagation();
                event.preventDefault();
            }
        },
        clear() {
            if (this.multiple) {
                this.$emit('input', []);
            } else {
                this.$emit('input', null);
            }
        },
    },
}
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/Select.vue